import store from "../store";
import authSlice from "./authSlice";
import { apiFetch, saveTokens, clearTokens, getRefreshToken } from "../../utils/api";

const {
  startLoading,
  stopLoading,
  loginSuccess,
  registerSuccess,
  setUser,
  authFailure,
  logout,
} = authSlice.actions;

// -----------------------------
// LOGIN THUNK
// -----------------------------
export const loginUser =
  (payload: { email: string; password: string }) =>
  async (dispatch: typeof store.dispatch) => {
    try {
      dispatch(startLoading());
      const data = await apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      // { token, refreshToken }
      saveTokens(data.token, data.refreshToken);
      dispatch(loginSuccess(data.token));
      dispatch(getUserProfile());
    } catch (err: any) {
      dispatch(authFailure(err.message || "Login failed"));
    }
  };

// -----------------------------
// REGISTER THUNK
// -----------------------------
export const registerUser =
  (payload: {
    email: string;
    password: string;
    phone: string;
    roles: string[];
  }) =>
  async (dispatch: typeof store.dispatch) => {
    try {
      dispatch(startLoading());
      const data = await apiFetch("/auth/addNewUser", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      // user object
      dispatch(registerSuccess(data));
    } catch (err: any) {
      dispatch(authFailure(err.message || "Registration failed"));
    }
  };

// -----------------------------
// PHONE OTP THUNKS
// -----------------------------
export const requestPhoneOtp =
  (phone: string) => async (dispatch: typeof store.dispatch) => {
    try {
      dispatch(startLoading());
      await apiFetch("/auth/phone/request-otp", {
        method: "POST",
        body: JSON.stringify({ phone }),
      });
      dispatch(stopLoading());
    } catch (err: any) {
      dispatch(authFailure(err.message || "Failed to send verification code"));
      throw err;
    }
  };

export const verifyPhoneOtp =
  (phone: string, otp: string) => async (dispatch: typeof store.dispatch) => {
    try {
      dispatch(startLoading());
      const data = await apiFetch("/auth/phone/verify-otp", {
        method: "POST",
        body: JSON.stringify({ phone, otp }),
      });
      // { token, refreshToken }
      saveTokens(data.token, data.refreshToken);
      dispatch(loginSuccess(data.token));
      dispatch(getUserProfile());
    } catch (err: any) {
      dispatch(authFailure(err.message || "Invalid or expired code"));
      throw err;
    }
  };

// -----------------------------
// LOGOUT THUNK
// -----------------------------
export const logoutUser = () => (dispatch: typeof store.dispatch) => {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    // Revoke the session server-side; log out locally even if this fails
    apiFetch("/auth/logout", {
      method: "POST",
      body: JSON.stringify({ refreshToken }),
    }).catch(() => {});
  }
  clearTokens();
  dispatch(logout());
};

// -----------------------------
// GET USER PROFILE THUNK
// -----------------------------
export const getUserProfile = () => async (dispatch: typeof store.dispatch) => {
  try {
    const data = await apiFetch("/auth/user/profile", {}, true); // auth = true
    dispatch(setUser(data));
    dispatch(stopLoading());
  } catch (err: any) {
    dispatch(authFailure(err.message || "Failed to fetch profile"));
  }
};
